import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarClock,
  Edit3,
  Mail,
  Building2,
  Briefcase,
  Users,
  TrendingUp,
  Plus,
  Star,
  MapPin,
  FileText,
  Truck,
  ClipboardList,
  Undo2,
} from 'lucide-react';
import {
  customersApi,
  contactsApi,
  tasksApi,
  anfragenApi,
  type Anfrage,
  type Task,
} from '@/lib/api';
import { mitHerkunft, type Herkunft } from '@/lib/herkunft';
import { AnsprechpartnerKarte, kontaktAus } from '@/components/Ansprechpartner';
import {
  Angaben,
  Button,
  Card,
  DataTable,
  EmptyState,
  Kontakt,
  ListRow,
  Skeleton,
  StatTile,
  Status,
  StatusPill,
  Tabs,
  TabsContent,
  formatDatum,
  formatEuro,
  useMobil,
} from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { ActivityTimeline } from '@/features/interactions/components/ActivityTimeline';
import { EmailHistory } from '@/components/emails/EmailHistory';
import { InteractionSheet } from '@/features/interactions/components/InteractionSheet';
import { ScheduleLinkDialog } from '@/features/customers/components/ScheduleLinkDialog';
import { TaskCard } from '@/features/tasks/components/TaskCard';
import { TaskSheet } from '@/features/tasks/components/TaskSheet';
import { useAufgabeErledigen } from '@/features/tasks/useAufgabeErledigen';
import { StatusSelect } from '@/features/customers/components/StatusSelect';
import { ZurueckZumLeadDialog } from '@/features/customers/components/ZurueckZumLeadDialog';
import { TagToggleButton } from '@/features/customers/components/TagToggleButton';
import { customerBusinessTypeLabel, LEAD_STATUSES } from '@/types';
import type {
  Contact,
  CustomerStatus,
  RelatedObject,
  RelatedProject,
  RelatedQuote,
  RelatedMission,
} from '@/types';

interface LinkedPartner {
  id: string;
  name: string;
  partner_number: string;
  partner_type: string | null;
}

const industryLabels: Record<string, string> = {
  privat_immobilien: 'Privat Immobilien',
  immobilien: 'Immobilien',
  industrie: 'Industrie',
  oeffentlich: 'Öffentlich',
  gewerbe: 'Gewerbe',
  wohnungswirtschaft: 'Wohnungswirtschaft',
};

const acquisitionLabels: Record<string, string> = {
  website: 'Website',
  empfehlung: 'Empfehlung',
  messe: 'Messe',
  kaltakquise: 'Kaltakquise',
  partner: 'Partner',
  bestandskunde: 'Bestandskunde',
  social_media: 'Social Media',
  sonstige: 'Sonstige',
};

type TabId = 'contacts' | 'activities' | 'emails' | 'tasks' | 'anfragen' | 'objects' | 'projects' | 'quotes' | 'missions';

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Am Handy stehen die Ansprechpartner oben in einer eigenen Karte; der
  // erste Reiter ist dort der Verlauf. Nur der Start, ein Drehen wechselt ihn nicht.
  const mobil = useMobil();
  const [activeTab, setActiveTab] = useState<TabId>(mobil ? 'activities' : 'contacts');
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [terminOffen, setTerminOffen] = useState(false);
  const [aufgabeOffen, setAufgabeOffen] = useState(false);
  // Rueckweg zum Lead: Rueckfrage mit Zahlen, kein window.confirm (Hausregel).
  const [rueckwegOffen, setRueckwegOffen] = useState(false);
  // Dasselbe Sheet legt an und bearbeitet. `null` heisst anlegen; beim
  // Schliessen zurueck auf `null`, sonst oeffnet „Aufgabe anlegen" beim
  // naechsten Mal die zuletzt bearbeitete Aufgabe.
  const [bearbeiteteAufgabe, setBearbeiteteAufgabe] = useState<Task | null>(null);
  const erledigen = useAufgabeErledigen();

  // Von dieser Seite aus geklickt: Kontakte tragen den Kunden als Herkunft
  // mit, damit der Weg zurueck nach dem Bearbeiten hierher fuehrt und nicht
  // in die allgemeine Kontaktliste (siehe lib/herkunft.ts).
  const herkunft: Herkunft = { customerId: id };

  const aufgabeAnlegen = () => {
    setBearbeiteteAufgabe(null);
    setAufgabeOffen(true);
  };

  const aufgabeBearbeiten = (task: Task) => {
    setBearbeiteteAufgabe(task);
    setAufgabeOffen(true);
  };

  const { data: customer, isPending, isFetching } = useQuery({
    queryKey: ['customers', id],
    queryFn: () => customersApi.get(id!),
    enabled: !!id,
  });

  const { data: contacts } = useQuery({
    queryKey: ['contacts', { customer_id: id }],
    queryFn: () => contactsApi.list({ customer_id: id }),
    enabled: !!id,
  });

  // Load tasks with hierarchical visibility (includes project/deal tasks)
  const { data: tasks } = useQuery({
    queryKey: ['tasks', 'entity', 'customer', id],
    queryFn: () => tasksApi.getForEntity('customer', id!, true),
    enabled: !!id && activeTab === 'tasks',
  });

  const { data: anfragen } = useQuery({
    queryKey: ['anfragen', { customer_id: id }],
    queryFn: () => anfragenApi.list(),
    enabled: !!id && activeTab === 'anfragen',
    select: (data) => data.filter(a => a.customer_id === id),
  });

  // Gebaeude, Projekte, Angebote, Einsaetze des Kunden aus den gemeinsamen
  // Tabellen. Bis zum 22.10.2026 lieferte der Endpunkt Beispieldaten und die
  // Seite rief ihn deshalb bewusst nicht auf (Audit C7).
  const { data: relatedData } = useQuery({
    queryKey: ['customers', id, 'related'],
    queryFn: () => customersApi.getRelated(id!),
    enabled: !!id,
  });

  const { data: linkedPartner } = useQuery<LinkedPartner | null>({
    queryKey: ['customers', id, 'linked-partner'],
    queryFn: () => customersApi.getLinkedPartner(id!),
    enabled: !!id,
  });

  const primaryContact = useMemo(() => {
    return contacts?.items?.find(c => c.is_primary);
  }, [contacts]);

  if (isPending) {
    return (
      <div className="space-y-6 pt-4">
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 rounded-lg" />
          <div className="flex-1">
            <Skeleton className="mb-2 h-8 w-64" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Skeleton className="h-48 rounded-lg lg:col-span-2" />
          <Skeleton className="h-48 rounded-lg" />
        </div>
      </div>
    );
  }

  // Ein Lead ist ein Kunde im Lead-Status, GET /customers/{id} liefert ihn
  // auch. Die Suche führt seit dem 22.10.2026 direkt zu /leads/:id; hierher
  // kommt ein Lead noch über ältere Links (Aufgabe, Interaktion, Lesezeichen)
  // und gehört auf seine Seite. Nicht während eines Neuladens: Nach dem
  // Umwandeln stünde sonst kurz der alte Status im Cache und schickte den
  // frischen Kunden zurück zu den Leads.
  if (customer && !isFetching && LEAD_STATUSES.includes(customer.status)) {
    return <Navigate to={`/leads/${customer.id}`} replace />;
  }

  if (!customer) {
    return (
      <>
        <CrmPageHeader title="Kunde" zurueck={{ to: '/customers', label: 'Zur Kundenliste' }} />
        <EmptyState
          icon={Users}
          title="Kunde nicht gefunden"
          description="Der gesuchte Kunde existiert nicht oder wurde gelöscht."
          action={<Button onClick={() => navigate('/customers')}>Zur Kundenliste</Button>}
        />
      </>
    );
  }

  const tabs = [
    { id: 'contacts' as TabId, label: 'Kontakte', count: contacts?.total ?? 0 },
    { id: 'activities' as TabId, label: 'Aktivitäten' },
    { id: 'emails' as TabId, label: 'E-Mails' },
    { id: 'tasks' as TabId, label: 'Aufgaben', count: tasks?.length ?? 0 },
    { id: 'anfragen' as TabId, label: 'Anfragen', count: anfragen?.length ?? 0 },
    // Aus den gemeinsamen Tabellen (GET /customers/{id}/related)
    { id: 'objects' as TabId, label: 'Objekte', count: relatedData?.object_count ?? 0 },
    { id: 'projects' as TabId, label: 'Projekte', count: relatedData?.project_count ?? 0 },
    { id: 'quotes' as TabId, label: 'Angebote', count: relatedData?.quote_count ?? 0 },
    { id: 'missions' as TabId, label: 'Einsätze', count: relatedData?.mission_count ?? 0 },
  ];


  // Fehlt dem Kunden eine Angabe, steht die des primären Ansprechpartners da.
  const kontaktdaten = {
    email: customer.email || primaryContact?.email,
    telefon: customer.phone || primaryContact?.phone,
    mobil: !customer.phone && !primaryContact?.phone ? primaryContact?.mobile : undefined,
    website: customer.website,
  };

  return (
    <div className="space-y-6">
      <CrmPageHeader
        size="page"
        zurueck={{ to: '/customers', label: 'Zur Kundenliste' }}
        title={customer.company_name}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            {customer.display_name && <span>{customer.display_name}</span>}
            {customer.assigned_user_name && (
              <span className="inline-flex items-center gap-1.5">
                <Users size={14} strokeWidth={1.75} aria-hidden />
                {customer.assigned_user_name}
              </span>
            )}
            {linkedPartner && (
              <Link to={`/partners/${linkedPartner.id}`} className="rounded-full">
                <StatusPill tone="info">Auch Partner</StatusPill>
              </Link>
            )}
          </span>
        }
        mehrAktionen={[
          { label: 'Besichtigung planen', icon: CalendarClock, onSelect: () => setTerminOffen(true) },
          { label: 'Bearbeiten', icon: Edit3, onSelect: () => navigate(`/customers/${id}/edit`) },
          // Gegenstueck zu „Als Kunde anlegen" am Lead. Bis zum 29.10.2026 gab
          // es diesen Weg nicht, und wer ihn suchte, loeschte den Kunden —
          // neun Saetze zwischen 08:42 und 09:22 an diesem Tag.
          { label: 'Wieder als Lead führen', icon: Undo2, onSelect: () => setRueckwegOffen(true) },
        ]}
      >
        <div className="flex flex-wrap items-start gap-3 pb-2">
          <StatusSelect
            customerId={customer.id}
            currentStatus={customer.status as CustomerStatus}
            currentLostReason={customer.lost_reason}
            currentFollowUpAt={customer.follow_up_at}
          />
          <TagToggleButton customerId={customer.id} currentTags={customer.tags ?? []} />
        </div>
      </CrmPageHeader>

      {/* Am Handy: alle Ansprechpartner mit Anrufen und E-Mail zum Antippen */}
      <AnsprechpartnerKarte contacts={contacts?.items ?? []} herkunft={herkunft} className="md:hidden" />

      {/* Primary Contact */}
      {primaryContact && (
        <Card icon={Star} title="Primärer Ansprechpartner" className="max-md:hidden">
          <Kontakt darstellung="knoepfe" {...kontaktAus(primaryContact, herkunft)} primaer={false} />
        </Card>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Kontakte" value={contacts?.total ?? 0} icon={Users} />
        <StatTile label="Objekte" value={customer.managed_objects_count || 0} icon={Building2} />
        <StatTile
          label="Offene Anfragen"
          value={anfragen?.filter((a) => a.status !== 'GEWONNEN' && a.status !== 'VERLOREN').length ?? 0}
          icon={TrendingUp}
        />
        <StatTile label="Aufgaben" value={tasks?.filter((t) => t.status !== 'completed').length ?? 0} icon={Briefcase} />
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" title="Kontaktdaten" icon={Mail}>
          {kontaktdaten.email || kontaktdaten.telefon || kontaktdaten.mobil || kontaktdaten.website ? (
            <Kontakt {...kontaktdaten} />
          ) : (
            <div className="py-4 text-center t-small text-muted-foreground">Keine Kontaktdaten hinterlegt</div>
          )}
        </Card>

        <Card title="Geschäftsdaten" icon={Briefcase}>
          <Angaben
            items={[
              { label: 'Kundentyp', value: customerBusinessTypeLabel(customer.customer_business_type) },
              { label: 'Branche', value: customer.industry && (industryLabels[customer.industry] || customer.industry) },
              {
                label: 'Akquisequelle',
                value: customer.acquisition_source && (acquisitionLabels[customer.acquisition_source] || customer.acquisition_source),
              },
              { label: 'USt-IdNr.', value: customer.vat_number, mono: true },
              { label: 'Verwaltete Objekte', value: customer.managed_objects_count, mono: true },
            ]}
          />
        </Card>
      </div>

      {/* Notizen */}
      {customer.notes && (
        <Card title="Notizen" icon={FileText}>
          <p className="whitespace-pre-wrap">{customer.notes}</p>
        </Card>
      )}

      {/* Tabs */}
      <Card flush>
        <Tabs
          label="Bereiche des Kunden"
          className="[&>[role=tablist]]:px-5"
          value={activeTab}
          onChange={(v) => setActiveTab(v as TabId)}
          items={tabs.map((tab) => ({ value: tab.id, label: tab.label, count: tab.count }))}
        >
          <TabsContent value="contacts" className="p-5">
            <div className="mb-4 flex justify-end">
              <Button size="sm" icon={Plus} onClick={() => navigate(mitHerkunft('/contacts/new', herkunft))}>
                Kontakt anlegen
              </Button>
            </div>
            <DataTable<Contact>
              label="Kontakte"
              rows={contacts?.items ?? []}
              onRowClick={(contact) => navigate(mitHerkunft(`/contacts/${contact.id}`, herkunft))}
              empty="Ansprechpartner erscheinen hier, sobald einer angelegt ist."
              columns={[
                {
                  key: 'name',
                  header: 'Name',
                  mobil: 'titel',
                  render: (contact) => (
                    <span className="flex items-center gap-2">
                      <span className="font-medium">
                        {contact.first_name} {contact.last_name}
                      </span>
                      {contact.is_primary && (
                        <span className="inline-flex items-center gap-1 t-label text-highlight">
                          <Star size={12} strokeWidth={1.75} aria-hidden />
                          Primär
                        </span>
                      )}
                    </span>
                  ),
                },
                { key: 'email', header: 'E-Mail', render: (c) => c.email || '–' },
                { key: 'phone', header: 'Telefon', mono: true, mobil: 'unter', render: (c) => c.phone || c.mobile || '–' },
                { key: 'role', header: 'Position', mobil: 'unter', render: (c) => c.role || '–' },
              ]}
            />
          </TabsContent>

          <TabsContent value="emails" className="space-y-4 p-5">
            <div className="flex justify-end">
              <Link to={`/emails?customer_id=${id}`} className="inline-flex items-center t-small font-medium text-link hover:underline touch:min-h-9">
                Alle E-Mails dieses Kunden
              </Link>
            </div>
            <EmailHistory customerId={id} />
          </TabsContent>

          <TabsContent value="activities" className="p-5">
            <ActivityTimeline entityType="customer" entityId={id!} onAddInteraction={() => setShowInteractionModal(true)} />
          </TabsContent>

          <TabsContent value="tasks" className="p-5">
            <div className="mb-4 flex justify-end">
              <Button size="sm" icon={Plus} onClick={aufgabeAnlegen}>
                Aufgabe anlegen
              </Button>
            </div>
            {tasks && tasks.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-border">
                {/* Dieselbe Zeile wie in „Aufgaben“ und der Übersicht, mit Erledigen
                    und dem Stift zum Bearbeiten. Nicht die ganze Zeile als
                    Klickfläche: Sie ist `checkable`, und `ListRow` schliesst
                    `onClick` mit `checkable` aus — die runde Checkbox wäre ein
                    Klickziel im Klickziel. Eine Aufgabe hakt man ab, einen
                    Eintrag öffnet man. */}
                {tasks.map((task) => (
                  <TaskCard key={task.id} task={task} onStatusChange={erledigen} onEdit={aufgabeBearbeiten} />
                ))}
              </div>
            ) : (
              <EmptyState icon={ClipboardList} title="Keine Aufgaben" description="Aufgaben zu diesem Kunden erscheinen hier, sobald eine angelegt ist." />
            )}
          </TabsContent>

          <TabsContent value="anfragen" className="p-5">
            {/* Anfragen anlegen am Rechner (Pipeline am Handy nur lesen). */}
            <div className="mb-4 flex justify-end max-md:hidden">
              <Button size="sm" icon={Plus} onClick={() => navigate(`/pipeline?customer_id=${id}`)}>
                Anfrage anlegen
              </Button>
            </div>
            {anfragen && anfragen.length > 0 ? (
              <DataTable<Anfrage>
                label="Anfragen"
                rows={anfragen}
                onRowClick={() => navigate('/pipeline')}
                columns={[
                  {
                    key: 'titel',
                    header: 'Anfrage',
                    strong: true,
                    mobil: 'titel',
                    render: (a) => a.kundenwunsch || a.sales_nachricht || 'Anfrage',
                  },
                  {
                    key: 'created_at',
                    header: 'Angelegt',
                    mono: true,
                    mobil: 'unter',
                    render: (a) => formatDatum(a.created_at),
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    mobil: 'status',
                    render: (a) => <Status status={a.status} variant="plain" />,
                  },
                  { key: 'value', header: 'Wert', align: 'end', mono: true, mobil: 'wert', render: (a) => formatEuro(a.value, { cent: true }) },
                ]}
              />
            ) : (
              <EmptyState icon={TrendingUp} title="Keine Anfragen" description="Anfragen dieses Kunden erscheinen hier, sobald eine angelegt ist." />
            )}
          </TabsContent>

          <TabsContent value="objects" className="p-5">
            <p className="mb-4 t-small text-muted-foreground">Gebäude des Kunden (aus dem PM)</p>
            {relatedData?.objects && relatedData.objects.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-border">
                {relatedData.objects.map((obj: RelatedObject) => (
                  <ListRow
                    key={obj.id}
                    leading={<Building2 size={20} strokeWidth={1.75} className="text-highlight" aria-hidden />}
                    title={obj.name}
                    meta={obj.address || '–'}
                    metaIcon={MapPin}
                    trailing={
                      <span className="t-small text-muted-foreground">
                        {obj.last_service
                          ? `Letzter Einsatz ${formatDatum(obj.last_service)}`
                          : 'Noch kein Einsatz'}
                      </span>
                    }
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon={Building2} title="Keine Objekte" description="Gebäude des Kunden erscheinen hier, sobald das PM eins anlegt." />
            )}
          </TabsContent>

          <TabsContent value="projects" className="p-5">
            {relatedData?.projects && relatedData.projects.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-border">
                {relatedData.projects.map((p: RelatedProject) => (
                  <ListRow
                    key={p.id}
                    leading={<Briefcase size={20} strokeWidth={1.75} className="text-highlight" aria-hidden />}
                    title={p.name}
                    meta={<span className="t-mono">{p.project_number}</span>}
                    trailing={<Status status={p.status} />}
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon={Briefcase} title="Keine Projekte" description="Projekte des Kunden erscheinen hier, sobald das PM eins anlegt." />
            )}
          </TabsContent>

          <TabsContent value="quotes" className="p-5">
            {relatedData?.quotes && relatedData.quotes.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-border">
                {relatedData.quotes.map((q: RelatedQuote) => (
                  <ListRow
                    key={q.id}
                    leading={<FileText size={20} strokeWidth={1.75} className="text-highlight" aria-hidden />}
                    title={q.title}
                    // Am Handy steht der Betrag in der Meta-Zeile, rechts nur der Status:
                    // „Angebot gesendet“ ließe dem Titel sonst keinen Platz.
                    meta={
                      <>
                        <span className="whitespace-nowrap t-mono">{q.quote_number}</span>
                        <span className="whitespace-nowrap t-mono text-foreground md:hidden">{formatEuro(q.total_value, { cent: true })}</span>
                      </>
                    }
                    trailing={
                      <span className="flex items-center gap-3">
                        <span className="t-mono text-foreground max-md:hidden">{formatEuro(q.total_value, { cent: true })}</span>
                        <Status status={`quote_${q.status}`} />
                      </span>
                    }
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon={FileText} title="Keine Angebote" description="Angebote erscheinen hier, sobald eins angelegt ist." />
            )}
          </TabsContent>

          <TabsContent value="missions" className="p-5">
            {relatedData?.missions && relatedData.missions.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-border">
                {relatedData.missions.map((m: RelatedMission) => (
                  <ListRow
                    key={m.id}
                    leading={<Truck size={20} strokeWidth={1.75} className="text-highlight" aria-hidden />}
                    title={m.name || m.object_name}
                    meta={
                      [m.object_name, m.flight_date ? formatDatum(m.flight_date) : 'Kein Flugdatum']
                        .filter(Boolean)
                        .join(' · ')
                    }
                    trailing={<Status status={`mission_${m.status}`} />}
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon={Truck} title="Keine Einsätze" description="Einsätze erscheinen hier, sobald einer geplant ist." />
            )}
          </TabsContent>
        </Tabs>
      </Card>

      <ScheduleLinkDialog customerId={customer.id} open={terminOffen} onOpenChange={setTerminOffen} />
      <TaskSheet
        open={aufgabeOffen}
        onOpenChange={(offen) => {
          setAufgabeOffen(offen);
          if (!offen) setBearbeiteteAufgabe(null);
        }}
        task={bearbeiteteAufgabe}
        // Nur beim Anlegen vorbelegen. Die Liste zeigt mit
        // `include_inherited` auch Aufgaben aus Projekten des Kunden; eine
        // davon zu bearbeiten darf sie nicht nebenbei an den Kunden haengen.
        customerId={bearbeiteteAufgabe ? undefined : customer.id}
        description={`Zu ${customer.company_name}`}
      />

      <InteractionSheet
        open={showInteractionModal}
        onOpenChange={setShowInteractionModal}
        title="Interaktion anlegen"
        description={`Zu ${customer.company_name}`}
        customerId={id}
      />

      <ZurueckZumLeadDialog
        customerId={customer.id}
        companyName={customer.company_name}
        open={rueckwegOffen}
        onOpenChange={setRueckwegOffen}
      />
    </div>
  );
}
